/**
 * Gap Fill Prompt Component
 * 
 * Shows a smart suggestion to fill detected gaps when resizing rooms
 */

import React, { useMemo, useEffect } from 'react';
import { Target, Check, X } from 'lucide-react';
import type { GapFillSuggestion } from '../utils/gapDetection';

interface GapFillPromptProps {
  suggestion: GapFillSuggestion;
  position: { x: number; y: number };
  onAccept: () => void;
  onReject: () => void;
  scale: number;
}

const GapFillPrompt: React.FC<GapFillPromptProps> = ({
  suggestion,
  position,
  onAccept,
  onReject,
  scale
}) => {
  const { gap, visualPreview } = suggestion;
  
  // Detect touch device
  const isTouchDevice = useMemo(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);
  
  // Haptic feedback when gap is detected (touch devices only)
  useEffect(() => {
    if (isTouchDevice && 'vibrate' in navigator) {
      // Subtle vibration to indicate gap detected
      navigator.vibrate(20);
    }
  }, [isTouchDevice]);
  
  // Position the prompt near the cursor (offset more for touch to avoid finger obscuring it)
  const promptStyle: React.CSSProperties = {
    position: 'absolute',
    left: isTouchDevice ? `${position.x - 100}px` : `${position.x + 20}px`,
    top: isTouchDevice ? `${position.y - 200}px` : `${position.y - 60}px`,
    transform: 'translateY(-100%)',
    zIndex: 10000,
    pointerEvents: 'auto'
  };
  
  return (
    <div style={promptStyle}>
      <div className="bg-white rounded-lg shadow-2xl border-2 border-green-500 p-3 min-w-[200px] animate-fadeIn">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <Target className="w-4 h-4 text-green-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">
              Auto-Fill Gap
            </div>
            <div className="text-xs text-gray-600">
              {visualPreview.message}
            </div>
          </div>
        </div>
        
        {/* Gap Info */}
        <div className="bg-green-50 rounded p-2 mb-2 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Area:</span>
            <span className="font-semibold text-gray-900">
              {gap.area.toFixed(2)} m²
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Confidence:</span>
            <span className={`font-semibold ${
              gap.confidence === 'high' ? 'text-green-600' :
              gap.confidence === 'medium' ? 'text-yellow-600' :
              'text-orange-600'
            }`}>
              {gap.confidence.toUpperCase()}
            </span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onAccept}
            className={`flex-1 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white font-medium rounded flex items-center justify-center gap-1 transition-colors ${
              isTouchDevice ? 'text-base py-3 px-4' : 'text-sm py-2 px-3'
            }`}
          >
            <Check className={isTouchDevice ? 'w-5 h-5' : 'w-4 h-4'} />
            Fill Gap
          </button>
          <button
            onClick={onReject}
            className={`bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-700 font-medium rounded flex items-center justify-center transition-colors ${
              isTouchDevice ? 'text-base py-3 px-4' : 'text-sm py-2 px-3'
            }`}
          >
            <X className={isTouchDevice ? 'w-5 h-5' : 'w-4 h-4'} />
          </button>
        </div>
        
        {/* Helper Text - Only show on non-touch devices */}
        {!isTouchDevice && (
          <div className="text-xs text-gray-500 mt-2 text-center">
            Press <kbd className="px-1 py-0.5 bg-gray-200 rounded">Space</kbd> to accept
          </div>
        )}
      </div>
      
      {/* Arrow pointing to gap */}
      <div 
        className="absolute top-full left-1/2 -translate-x-1/2"
        style={{ 
          width: 0, 
          height: 0, 
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: '10px solid #10B981',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
        }}
      />
    </div>
  );
};

export default GapFillPrompt;
